import React, { useState } from 'react';
import {
    AppBar,
    Box,
    Drawer,
    IconButton,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Toolbar,
    Typography,
    Button,
    useTheme,
    useMediaQuery,
    Divider,
    Container,
    ListItemButton,
    Avatar,
    Menu,
    MenuItem,
    Tooltip,
} from '@mui/material';
import {
    Menu as MenuIcon,
    Home as HomeIcon,
    Restaurant as RestaurantIcon,
    List as ListIcon,
    Person as PersonIcon,
    EmojiEvents as GameIcon,
    Close as CloseIcon,
    Logout as LogoutIcon,
    AccountCircle as AccountCircleIcon,
} from '@mui/icons-material';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';

interface NavigationItem {
    key: string;
    label: string;
    path: string;
    icon: React.ReactNode;
    onClick?: () => void;
}

const ResponsiveNavigation: React.FC = () => {
    const theme = useTheme();
    const { isAuthenticated, user, logout } = useAuth();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [userMenuAnchorEl, setUserMenuAnchorEl] = useState<null | HTMLElement>(null);
    const { t } = useTranslation();
    const location = useLocation();

    const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setUserMenuAnchorEl(event.currentTarget);
    };

    const handleUserMenuClose = () => {
        setUserMenuAnchorEl(null);
    };

    const handleLogout = () => {
        handleUserMenuClose();
        logout();
    };

    const navigationItems: NavigationItem[] = [
        { key: 'home', label: t('home'), path: '/home', icon: <HomeIcon /> },
        { key: 'preorder', label: t('preordershort'), path: '/pre-order', icon: <RestaurantIcon /> },
        { key: 'summary', label: t('summary'), path: '/summary', icon: <ListIcon /> },
        //{ key: 'takeaway', label: t('takeaway'), path: '/takeaway-order', icon: <RestaurantIcon /> },
        //{ key: 'takeawaysummary', label: t('takeawaySummary'), path: '/takeaway-summary', icon: <ListIcon /> },
        //{ key: 'game', label: t('Game'), path: '/game', icon: <GameIcon /> },
        //{ key: 'profile', label: t('profile'), path: '/profile', icon: <PersonIcon /> },
    ];

    const handleDrawerToggle = () => {
        setDrawerOpen(!drawerOpen);
    };

    const isActiveRoute = (path: string) => {
        return location.pathname === path;
    };

    const DrawerContent = () => (
        <Box
            sx={{
                width: 250,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
            }}
            role="navigation"
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 2,
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                }}
            >
                <Typography variant="h6" component="div">
                    BCS Employee Portal
                </Typography>
                <IconButton
                    color="inherit"
                    onClick={handleDrawerToggle}
                    sx={{ display: { md: 'none' } }}
                >
                    <CloseIcon />
                </IconButton>
            </Box>
            <Divider />
            {isAuthenticated ? (
                <>
                    <List sx={{ flexGrow: 1 }}>
                        {navigationItems.map((item) => (
                            <ListItem 
                                key={item.key} 
                                disablePadding
                                sx={{
                                    backgroundColor: isActiveRoute(item.path) 
                                        ? theme.palette.action.selected 
                                        : 'transparent'
                                }}
                            >
                                <ListItemButton
                                    component={Link}
                                    to={item.path}
                                    onClick={() => isMobile && handleDrawerToggle()}
                                    selected={isActiveRoute(item.path)}
                                >
                                    <ListItemIcon
                                        sx={{
                                            color: isActiveRoute(item.path)
                                                ? theme.palette.primary.main
                                                : theme.palette.text.primary
                                        }}
                                    >
                                        {item.icon}
                                    </ListItemIcon>
                                    <ListItemText 
                                        primary={item.label}
                                        primaryTypographyProps={{
                                            color: isActiveRoute(item.path)
                                                ? theme.palette.primary.main
                                                : theme.palette.text.primary
                                        }}
                                    />
                                </ListItemButton>
                            </ListItem>
                        ))}
                    </List>
                    <Divider />
                    <ListItem disablePadding>
                        <ListItemButton onClick={logout}>
                            <ListItemIcon>
                                <LogoutIcon />
                            </ListItemIcon>
                            <ListItemText primary={t('logout')} />
                        </ListItemButton>
                    </ListItem>
                </>
            ) : (
                <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
                    <Button
                        component={Link}
                        to="/login"
                        variant="contained"
                        color="primary"
                        fullWidth
                    >
                        {t('login')}
                    </Button>
                </Box>
            )}
            <Divider />
            <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">
                    © {new Date().getFullYear()} BCS
                </Typography>
            </Box>
        </Box>
    );

    return (
        <>
            <AppBar 
                position="fixed" 
                sx={{
                    zIndex: theme.zIndex.drawer + 1,
                }}
            >
                <Container maxWidth="xl">
                    <Toolbar 
                        disableGutters
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {isMobile && (
                                <IconButton
                                    color="inherit"
                                    aria-label="open drawer"
                                    edge="start"
                                    onClick={handleDrawerToggle}
                                    sx={{ mr: 2 }}
                                >
                                    <MenuIcon />
                                </IconButton>
                            )}
                            <Typography
                                variant="h6"
                                noWrap
                                component={Link}
                                to="/"
                                sx={{
                                    mr: 2,
                                    fontFamily: 'monospace',
                                    fontWeight: 700,
                                    letterSpacing: '.3rem',
                                    color: 'inherit',
                                    textDecoration: 'none',
                                }}
                            >
                                BCS
                            </Typography>
                        </Box>

                        {!isMobile && (
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                {isAuthenticated ? (
                                    <>
                                        {navigationItems.map((item) => (
                                            <Button
                                                key={item.key}
                                                component={Link}
                                                to={item.path}
                                                color="inherit"
                                                startIcon={item.icon}
                                                sx={{
                                                    position: 'relative',
                                                    '&::after': isActiveRoute(item.path) ? {
                                                        content: '""',
                                                        position: 'absolute',
                                                        bottom: 0,
                                                        left: 0,
                                                        width: '100%',
                                                        height: '3px',
                                                        backgroundColor: 'currentColor',
                                                        borderRadius: '2px 2px 0 0',
                                                    } : undefined,
                                                }}
                                            >
                                                {item.label}
                                            </Button>
                                        ))}
                                        
                                        <Tooltip title={t('profile')}>
                                            <IconButton
                                                onClick={handleUserMenuOpen}
                                                color="inherit"
                                                sx={{ ml: 2 }}
                                            >
                                                <Avatar 
                                                    sx={{ 
                                                        width: 32, 
                                                        height: 32,
                                                        bgcolor: theme.palette.secondary.main
                                                    }}
                                                >
                                                    {user?.name?.charAt(0) || <AccountCircleIcon />}
                                                </Avatar>
                                            </IconButton>
                                        </Tooltip>
                                        
                                        <Menu
                                            anchorEl={userMenuAnchorEl}
                                            open={Boolean(userMenuAnchorEl)}
                                            onClose={handleUserMenuClose}
                                            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                                            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                                        >
                                            <Box sx={{ px: 2, py: 1 }}>
                                                <Typography variant="subtitle1">{user?.name}</Typography>
                                                <Typography variant="body2" color="text.secondary">{user?.email}</Typography>
                                            </Box>
                                            <Divider />
                                            <MenuItem component={Link} to="/profile" onClick={handleUserMenuClose}>
                                                <ListItemIcon>
                                                    <PersonIcon fontSize="small" />
                                                </ListItemIcon>
                                                {t('profile')}
                                            </MenuItem>
                                            <MenuItem onClick={handleLogout}>
                                                <ListItemIcon>
                                                    <LogoutIcon fontSize="small" />
                                                </ListItemIcon>
                                                {t('logout')}
                                            </MenuItem>
                                        </Menu>
                                    </>
                                ) : (
                                    <Button
                                        component={Link}
                                        to="/login"
                                        variant="contained"
                                        color="secondary"
                                    >
                                        {t('login')}
                                    </Button>
                                )}
                            </Box>
                        )}
                    </Toolbar>
                </Container>
            </AppBar>

            <Box 
                component="nav" 
                sx={{ 
                    width: { md: 250 }, 
                    flexShrink: { md: 0 } 
                }}
            >
                {/* Mobile drawer */}
                <Drawer
                    variant="temporary"
                    anchor="left"
                    open={isMobile && drawerOpen}
                    onClose={handleDrawerToggle}
                    ModalProps={{
                        keepMounted: true, // Better mobile performance
                    }}
                    sx={{
                        display: { xs: 'block', md: 'none' },
                        '& .MuiDrawer-paper': { 
                            boxSizing: 'border-box', 
                            width: 250 
                        },
                    }}
                >
                    <DrawerContent />
                </Drawer>
            </Box>

            {/* Toolbar spacer */}
            <Toolbar />
        </>
    );
};

export default ResponsiveNavigation;